import React, { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { Loader2, Mail, Eye, X, AlertCircle, Send } from "lucide-react";

// Statuts de délivrabilité (jamais « envoyé » = « délivré »)
const DELIVERY = {
  delivered: { label: "Délivré", cls: "bg-emerald-100 text-emerald-700" },
  sent: { label: "Envoyé", cls: "bg-sky-100 text-sky-700" },
  delivery_delayed: { label: "Retardé", cls: "bg-amber-100 text-amber-700" },
  delayed: { label: "Retardé", cls: "bg-amber-100 text-amber-700" },
  bounced: { label: "Rejeté", cls: "bg-red-100 text-red-700" },
  failed: { label: "Échec", cls: "bg-red-100 text-red-700" },
  rejected: { label: "Rejeté", cls: "bg-red-100 text-red-700" },
  complained: { label: "Signalé comme indésirable", cls: "bg-orange-100 text-orange-700" },
};

function deliveryBadge(c) {
  if (c.status === "failed") return DELIVERY.failed;
  let key = (c.delivery_status || "").replace(/^email\./, "");
  if (!key) key = (c.status === "sent" || c.status === "simulated") ? "sent" : "unknown";
  return DELIVERY[key] || { label: "Inconnu", cls: "bg-slate-100 text-slate-600" };
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleDateString("fr-FR")} à ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
}

export default function BookingCommunications({ bookingId }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setError("");
    setItems(null);
    api.get(`/vendor/bookings/${bookingId}/communications`)
      .then(({ data }) => setItems(data.communications || []))
      .catch(() => setError("Impossible de charger l'historique des emails."));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [bookingId]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 overflow-hidden" data-testid="section-communications">
      <div className="flex items-center justify-between gap-2 mb-4">
        <div className="overline truncate">Communications</div>
        <Mail className="h-4 w-4 text-slate-400 shrink-0" />
      </div>

      {items === null && !error && (
        <div className="py-8 flex justify-center" data-testid="communications-loading">
          <Loader2 className="h-5 w-5 animate-spin text-violet-600" />
        </div>
      )}

      {error && (
        <div className="py-6 flex flex-col items-center gap-2 text-center" data-testid="communications-error">
          <AlertCircle className="h-5 w-5 text-red-500" />
          <p className="text-sm text-slate-500">{error}</p>
          <button onClick={load} className="text-xs font-semibold text-violet-600 hover:underline" data-testid="communications-retry">
            Réessayer
          </button>
        </div>
      )}

      {items && items.length === 0 && !error && (
        <div className="py-8 text-center text-sm text-slate-400" data-testid="communications-empty">
          Aucun email n'a encore été envoyé pour cette réservation.
        </div>
      )}

      {items && items.length > 0 && (
        <div className="space-y-2" data-testid="communications-list">
          {items.map((c) => {
            const badge = deliveryBadge(c);
            return (
              <div
                key={c.id}
                className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-violet-300 transition-colors"
                data-testid={`communication-row-${c.id}`}
              >
                <div className="h-9 w-9 rounded-full bg-violet-50 flex items-center justify-center shrink-0">
                  <Send className="h-4 w-4 text-violet-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-slate-900 truncate">{c.type_label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`} data-testid={`communication-status-${c.id}`}>
                      {badge.label}
                    </span>
                  </div>
                  {c.subject && <div className="text-xs text-slate-600 truncate">{c.subject}</div>}
                  <div className="text-[11px] text-slate-400 truncate">
                    {fmtDateTime(c.sent_at)} · {c.to}
                  </div>
                </div>
                <button
                  onClick={() => setOpenId(c.id)}
                  className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:border-slate-900 hover:bg-slate-50 transition-colors"
                  data-testid={`communication-view-${c.id}`}
                >
                  <Eye className="h-3.5 w-3.5" /> Voir l'email
                </button>
              </div>
            );
          })}
        </div>
      )}

      {openId && (
        <CommunicationModal
          bookingId={bookingId}
          logId={openId}
          onClose={() => setOpenId(null)}
        />
      )}
    </div>
  );
}

function CommunicationModal({ bookingId, logId, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    api.get(`/vendor/bookings/${bookingId}/communications/${logId}`)
      .then(({ data }) => setData(data.communication))
      .catch(() => setError("Impossible de charger cet email."));
  }, [bookingId, logId]);

  const badge = data ? deliveryBadge(data) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-[2px] p-3 sm:p-4"
      onClick={onClose}
      data-testid="communication-modal-overlay"
    >
      <div
        className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="communication-modal"
      >
        <div className="p-4 sm:p-5 border-b border-slate-200 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <div className="font-heading text-base sm:text-lg font-medium text-slate-900 truncate">
              {data ? data.type_label : "Email"}
            </div>
            {data && (
              <div className="text-xs text-slate-500 truncate">{data.subject}</div>
            )}
          </div>
          <button
            onClick={onClose}
            className="h-8 w-8 rounded-full border border-slate-200 flex items-center justify-center hover:bg-slate-50 shrink-0"
            aria-label="Fermer"
            data-testid="communication-modal-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="p-8 text-center text-sm text-slate-500" data-testid="communication-modal-error">{error}</div>
          )}

          {!data && !error && (
            <div className="p-12 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-600" /></div>
          )}

          {data && (
            <>
              <div className="px-4 sm:px-5 py-3 border-b border-slate-100 space-y-1.5 text-xs" data-testid="communication-modal-meta">
                <MetaRow label="Expéditeur" value={data.from_label || "—"} />
                <MetaRow label="Destinataire" value={data.to} />
                <MetaRow label="Envoyé le" value={fmtDateTime(data.sent_at)} />
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 w-24 shrink-0">Statut</span>
                  {badge && <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>{badge.label}</span>}
                </div>
              </div>
              <div className="p-4 sm:p-5">
                {data.has_content ? (
                  <EmailFrame html={data.snapshot_html} />
                ) : (
                  <div className="py-10 text-center text-sm text-slate-500" data-testid="communication-no-content">
                    Le contenu de cet email n'est pas disponible dans l'historique.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EmailFrame({ html }) {
  // Les emails ont une largeur mini (~600px). On les rend à leur largeur naturelle
  // puis on les met à l'échelle pour s'adapter à la largeur du téléphone (sans toucher au template).
  const EMAIL_W = 600;
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [displayH, setDisplayH] = useState(() => Math.round((typeof window !== "undefined" ? window.innerHeight : 800) * 0.52));

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      setScale(w > 0 && w < EMAIL_W ? w / EMAIL_W : 1);
      setDisplayH(Math.round(window.innerHeight * 0.52));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => { ro.disconnect(); window.removeEventListener("resize", update); };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="w-full overflow-hidden rounded-xl border border-slate-200 bg-white"
      style={{ height: displayH }}
      data-testid="communication-iframe-wrap"
    >
      <iframe
        title="Contenu de l'email"
        sandbox=""
        srcDoc={html}
        data-testid="communication-iframe"
        style={{
          width: EMAIL_W,
          height: scale ? displayH / scale : displayH,
          border: 0,
          transform: `scale(${scale})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
}

function MetaRow({ label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-slate-400 w-24 shrink-0">{label}</span>
      <span className="text-slate-700 font-medium truncate">{value}</span>
    </div>
  );
}
